/**
 * Pure rules for deciding whether an axios failure might mean "the API is not there",
 * and how long to wait between reconnect probes.
 *
 * Split out of helpers/apiStatus.js so the judgement can be unit-tested without a
 * browser, a Vue runtime, or a network. apiStatus.js owns the state machine and the
 * probing; this file owns only the two decisions that are easy to get subtly wrong.
 *
 * The classification is deliberately WIDE and deliberately non-committal: it answers
 * "is this worth checking?", never "is the server down?". A single request can fail for
 * reasons that have nothing to do with the server's health (one slow report hitting the
 * 30s axios timeout, a cancelled navigation), so a suspicion only ever starts a probe of
 * `/health`. The probe — not this function — decides whether the user sees anything.
 * That ordering is what keeps a routine slow query from throwing a full-screen
 * "under maintenance" panel over someone's work.
 */

/** Statuses IIS / a reverse proxy returns while the app pool behind it is down. */
export const OUTAGE_STATUSES = [502, 503, 504];

/**
 * Axios `error.code` values that mean the request never got an HTTP answer.
 * `undefined` counts too: older axios builds and non-axios rejections carry no code.
 */
const NO_RESPONSE_CODES = [
  "ERR_NETWORK",
  "ECONNABORTED", // axios timeout
  "ECONNREFUSED",
  "ECONNRESET",
  "ETIMEDOUT",
  "EAI_AGAIN",
];

/** Cancelled requests are our own doing — never a symptom of anything. */
const CANCEL_CODES = ["ERR_CANCELED", "ECONNABORTED_CANCELLED"];

export function isOutageStatus(status) {
  return OUTAGE_STATUSES.includes(Number(status));
}

/**
 * @param {any} error axios error (or anything a rejected interceptor was handed)
 * @returns {"suspect"|"ignore"} `"suspect"` means: probe /health before concluding anything.
 */
export function classifyApiFailure(error) {
  if (!error) return "ignore";

  // Per-request opt-out, for callers that expect and handle their own failures.
  if (error.config?.meta?.ignoreOutage) return "ignore";

  const code = error.code;
  if (CANCEL_CODES.includes(code)) return "ignore";
  // axios sets this on `CanceledError`; also covers a manual AbortController abort.
  if (error.name === "CanceledError" || error.name === "AbortError") return "ignore";

  const status = error.response?.status;
  if (status != null) {
    // The server answered. Only the gateway-level statuses are worth a probe —
    // a 400/401/403/404/409/500 all prove the API is up and routing.
    return isOutageStatus(status) ? "suspect" : "ignore";
  }

  // No HTTP response at all: connection refused, DNS gone, TLS reset, or a timeout.
  if (code == null || NO_RESPONSE_CODES.includes(code)) return "suspect";
  return "ignore";
}

/**
 * Reconnect backoff. Deploys finish in tens of seconds, so the ladder starts fast and
 * settles at a 15s ceiling rather than growing without bound — an operator staring at
 * the overlay should never wait minutes past the moment the API came back.
 */
export const PROBE_DELAYS_MS = [2000, 3000, 5000, 8000, 12000, 15000];

/**
 * @param {number} attempt zero-based count of probes already made in this outage
 * @returns {number} milliseconds to wait before the next probe
 */
export function nextProbeDelay(attempt) {
  const n = Number.isFinite(attempt) && attempt > 0 ? Math.floor(attempt) : 0;
  return PROBE_DELAYS_MS[Math.min(n, PROBE_DELAYS_MS.length - 1)];
}
